import React from 'react';
import GdprSettings from 'tradera-utils/gdpr-settings';
import { useSelector } from 'react-redux';
import { useTranslator } from 'tradera-lang/translate';
import { loadScript } from 'tradera-utils/script';

const LOCALES = {
    sv: 'sv-se',
    en: 'en-us',
    da: 'da-dk',
    de: 'de-de'
};

const mapLocale = languageCodeIso2 =>
    languageCodeIso2 in LOCALES ? LOCALES[languageCodeIso2] : LOCALES['sv'];

const openQualtiveModal = async (config, translate) => {
    const { survey, title, ...surveyOptions } = config;
    const options = {
        title: translate(title ? title : 'qualtiveTitle_default'),
        darkMode: 'never',
        ...surveyOptions
    };

    if (!window.qualtive) {
        await loadScript('https://static.qualtive.io/client/web.v1.js');
    }
    window.qualtive?.present(survey, options);
};

export const useQualtive = (customAttributes = {}) => {
    const { t } = useTranslator();
    const isQualtiveFeatureToggleEnabled = useSelector(
        state => state.environment?.featureSwitches?.['qualtive-feedback']
    );
    const memberId = useSelector(state => state.member?.memberId);
    const languageCodeIso2 = useSelector(
        state => state.language?.preferred?.languageCodeIso2
    );
    const isQualtiveEnabled =
        isQualtiveFeatureToggleEnabled && GdprSettings.isFunctionalEnabled();
    const config = {
        locale: mapLocale(languageCodeIso2),
        customAttributes
    };
    if (memberId) {
        config.user = { id: memberId };
    }

    return {
        isQualtiveEnabled,
        openQualtiveModal: survey => {
            if (isQualtiveEnabled) {
                openQualtiveModal(
                    {
                        ...config,
                        ...survey
                    },
                    t
                );
            }
        }
    };
};

export const withQualtive = WrappedComponent => {
    function WithQualtive(props) {
        const { isQualtiveEnabled, openQualtiveModal } = useQualtive();
        return (
            <WrappedComponent
                isQualtiveEnabled={isQualtiveEnabled}
                openQualtiveModal={openQualtiveModal}
                {...props}
            />
        );
    }

    WithQualtive.displayName = `withQualtive(${WrappedComponent.displayName ||
        WrappedComponent.name}`;

    return WithQualtive;
};
